import React, { useState, useContext } from 'react'

import { Button, FormControl, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import usersApi from '../api/users'
import PasswordInput from '../components/inputs/PasswordInput'
import { AppSettingsContext } from '../App'
import { removeToken, removeRefreshToken } from '../useToken'
import useRole from '../useUserRole'
import { removeSessionFilters } from '../useSessionFilters'
import { setSystemMessage } from '../features/systemMessageSlice'
import { getApiErrorMessage, getApiFieldErrorMessage } from '../utils/apiErrors'
import { PASSWORD_REQUIREMENTS_MESSAGE, validateStrongPassword } from '../utils/passwordValidation'
import {
  ProfilePageCard,
  ProfilePageDescription,
  ProfilePageForm,
  ProfilePageHint,
  ProfilePageMain,
  ProfilePageTitle,
  ProfilePageDangerZone,
  ProfilePageDangerTitle,
  ProfilePageDangerDescription,
  ProfilePageUserInfoSection,
  ProfilePageUsername,
  ProfilePageUsernameValue,
} from '../styles/pages/ProfilePageStyle'

const emptyValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

const emptyErrors = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

const ProfilePage = () => {
  const [values, setValues] = useState(emptyValues)
  const [errors, setErrors] = useState(emptyErrors)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deletePasswordError, setDeletePasswordError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { removeUserRole } = useRole()
  const { setIsLoggedIn } = useContext(AppSettingsContext)
  const username = sessionStorage.getItem('username')

  const validateField = (name, value, currentValues) => {
    switch (name) {
      case 'currentPassword':
        return value !== '' ? '' : 'Enter your current password'
      case 'newPassword':
        return validateStrongPassword(value)
      case 'confirmPassword':
        return value !== currentValues.newPassword ? 'Passwords must match' : ''
      default:
        return ''
    }
  }

  const isAnyInputInvalid = (nextErrors) => {
    return !Object.values(nextErrors).every((error) => !error)
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    const nextValues = {
      ...values,
      [name]: value,
    }

    setValues(nextValues)
    setErrors({
      ...errors,
      [name]: validateField(name, value, nextValues),
      ...(name === 'newPassword'
        ? { confirmPassword: validateField('confirmPassword', nextValues.confirmPassword, nextValues) }
        : {}),
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const nextErrors = {
      currentPassword: validateField('currentPassword', values.currentPassword, values),
      newPassword: validateField('newPassword', values.newPassword, values),
      confirmPassword: validateField('confirmPassword', values.confirmPassword, values),
    }

    setErrors(nextErrors)
    if (isAnyInputInvalid(nextErrors)) {
      return
    }

    setIsSubmitting(true)

    await usersApi
      .changeOwnPassword(values.currentPassword, values.newPassword)
      .then(() => {
        setValues(emptyValues)
        setErrors(emptyErrors)
        dispatch(
          setSystemMessage({
            severity: 'success',
            message: 'Password was updated successfully',
          })
        )
      })
      .catch((error) => {
        const errorData = error?.response?.data
        const currentPasswordError = getApiFieldErrorMessage(errorData, 'current_password')
        const newPasswordError = getApiFieldErrorMessage(errorData, 'new_password')

        setErrors((previousErrors) => ({
          ...previousErrors,
          ...(currentPasswordError ? { currentPassword: currentPasswordError } : {}),
          ...(newPasswordError ? { newPassword: newPasswordError } : {}),
        }))

        dispatch(
          setSystemMessage({
            severity: 'error',
            message: getApiErrorMessage(error, 'Unable to update password'),
          })
        )
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
    setDeletePassword('')
    setDeletePasswordError('')
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setDeletePassword('')
    setDeletePasswordError('')
  }

  const handleDeleteConfirm = async () => {
    if (!deletePassword) {
      setDeletePasswordError('Enter your password')
      return
    }

    setIsDeleting(true)

    await usersApi
      .deleteOwnAccount(deletePassword)
      .then(() => {
        // Perform logout
        removeToken()
        removeRefreshToken()
        removeUserRole()
        sessionStorage.removeItem('username')
        removeSessionFilters()
        setIsLoggedIn(false)

        dispatch(
          setSystemMessage({
            severity: 'success',
            message: 'Account deleted successfully. Redirecting to login...',
          })
        )

        // Navigate to login
        navigate('/login')
      })
      .catch((error) => {
        const errorData = error?.response?.data
        const passwordError = getApiFieldErrorMessage(errorData, 'password')

        if (passwordError) {
          setDeletePasswordError(passwordError)
        } else {
          dispatch(
            setSystemMessage({
              severity: 'error',
              message: getApiErrorMessage(error, 'Unable to delete account'),
            })
          )
        }
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }

  return (
    <ProfilePageMain>
      <ProfilePageCard elevation={4}>
        <ProfilePageTitle variant="h4">Profile</ProfilePageTitle>

        {/* User Info Section */}
        <ProfilePageUserInfoSection>
          <ProfilePageUsername variant="body2">Logged in as:</ProfilePageUsername>
          <ProfilePageUsernameValue variant="h5">{username}</ProfilePageUsernameValue>
        </ProfilePageUserInfoSection>

        {/* Password Change Section */}
        <ProfilePageDescription variant="body1">Change your password for the current account.</ProfilePageDescription>
        <ProfilePageForm onSubmit={handleSubmit}>
          <FormControl variant="outlined">
            <PasswordInput
              name="currentPassword"
              label="Current Password"
              value={values.currentPassword}
              handleChange={handleChange}
              errorMessage={errors.currentPassword}
              autocomplete="current-password"
            />
          </FormControl>
          <FormControl variant="outlined">
            <PasswordInput
              name="newPassword"
              label="New Password"
              value={values.newPassword}
              handleChange={handleChange}
              errorMessage={errors.newPassword}
              autocomplete="new-password"
            />
          </FormControl>
          <ProfilePageHint>{PASSWORD_REQUIREMENTS_MESSAGE}</ProfilePageHint>
          <FormControl variant="outlined">
            <PasswordInput
              name="confirmPassword"
              label="Confirm New Password"
              value={values.confirmPassword}
              handleChange={handleChange}
              errorMessage={errors.confirmPassword}
              autocomplete="new-password"
            />
          </FormControl>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Change Password
          </Button>
        </ProfilePageForm>

        {/* Danger Zone - Delete Account */}
        <ProfilePageDangerZone>
          <ProfilePageDangerTitle variant="h5">Danger Zone</ProfilePageDangerTitle>
          <ProfilePageDangerDescription variant="body2">
            Deleting your account will permanently remove all your data. This cannot be undone.
          </ProfilePageDangerDescription>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteClick}
          >
            Delete Account
          </Button>
        </ProfilePageDangerZone>
      </ProfilePageCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <ProfilePageDangerDescription variant="body2" sx={{ mb: 2, mt: 1 }}>
            This action is permanent and cannot be reversed. All your data will be deleted.
          </ProfilePageDangerDescription>
          <FormControl variant="outlined" fullWidth>
            <PasswordInput
              name="deletePassword"
              label="Enter your password to confirm"
              value={deletePassword}
              handleChange={(e) => {
                setDeletePassword(e.target.value)
                setDeletePasswordError('')
              }}
              errorMessage={deletePasswordError}
              autocomplete="current-password"
            />
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfilePageMain>
  )
}

export default ProfilePage